/**
 * OKS-66 — Inertia prop shapes for the admin-only Platform Analytics
 * surface at `/admin/analytics`.
 *
 * Mirrors the server-side `AdminOps::PlatformAnalytics.Result` struct
 * 1:1. All keys are snake_case (the Inertia JSON round-trip preserves
 * the Ruby serializer's keys).
 *
 * # 3-state matrix (FE rendering branch)
 *
 *   - `"empty"`   — `Brand.count == 0 && Agency.count == 0 &&
 *                   User.creator_role.count == 0`. Full-page
 *                   `<AnalyticsEmptyState>` card; all data props null.
 *   - `"success"` — All sections populated. The "zero signups in window"
 *                   case is detected FE-side from `growth.series.every`
 *                   zero-check and swaps the chart card to its empty
 *                   variant — NOT a service-level state.
 *   - `"error"`   — Aggregator raised; `rescue StandardError` branch on
 *                   the controller. All data props null; FE renders
 *                   `<AnalyticsErrorState>` with a Retry button.
 *
 * # Brand-isolation NOTE (intentional exception)
 *
 * This surface aggregates across all tenants by design — admin staff
 * see brand names directly. The "brand-isolation P0" hard rule binds
 * tenant-facing surfaces only (PRD §6 + CLAUDE.md). LQE QA Plan NI1–NI3
 * test the inverse (admin DOES see all brands).
 */

// =========================================================================
// Window whitelist — mirror `AdminOps::PlatformAnalytics::WINDOW_VALUES`
// =========================================================================

export const WINDOW_VALUES = ["30d", "90d", "all"] as const
export type AnalyticsWindow = (typeof WINDOW_VALUES)[number]
export const DEFAULT_WINDOW: AnalyticsWindow = "30d"

// =========================================================================
// State discriminator
// =========================================================================

export type AdminAnalyticsState = "empty" | "success" | "error"

// =========================================================================
// KPIs (flat Hash — see TRD K5.1)
// =========================================================================

/**
 * Flat KPI Hash — snapshot counts (window-invariant) + `active_brands`
 * (window-scoped) live side-by-side. `active_brands_window` echoes the
 * resolved server-side window so the FE renders the tile caption
 * ("in last {window}") from the server's truth, not URL state (which
 * may include off-whitelist values the server coerced to "30d").
 */
export interface AdminAnalyticsKpis {
  brands: number
  agencies: number
  creators: number
  active_brands: number
  active_brands_window: AnalyticsWindow
}

// =========================================================================
// Overlap (cross-brand creator distribution — see TRD K7)
// =========================================================================

/**
 * Stable bucket identifier. FE keys i18n strings off these labels
 * (`admin.analytics.overlap.bucket_one` / `bucket_two` / `bucket_three_plus`).
 * NEVER raw display strings.
 */
export type AdminAnalyticsOverlapBucketLabel = "one" | "two" | "three_plus"

export interface AdminAnalyticsOverlapBucket {
  label: AdminAnalyticsOverlapBucketLabel
  count: number
  /**
   * Share of total creators ∈ [0, 1].
   *
   * `null` only when `total_creators === 0` (FE renders `—%`).
   * `0` means `0 / N` with `N > 0` (FE renders `0.0%`). Distinct
   * semantics — matches OKS-63 `acceptance_rate: nil` convention.
   */
  share: number | null
}

export interface AdminAnalyticsOverlap {
  multi_brand_creators: number
  total_creators: number
  /** Always 3 buckets in fixed order: `one`, `two`, `three_plus`. */
  buckets: AdminAnalyticsOverlapBucket[]
}

// =========================================================================
// Growth chart (flat-per-day, zero-filled — see TRD K6)
// =========================================================================

/**
 * Recharts-feed-shaped row. One row per UTC day in `range.start..range.end`
 * inclusive. Days with no signups emit `{brands: 0, agencies: 0, creators: 0}`
 * (no gaps). Date format `YYYY-MM-DD` ISO 8601.
 */
export interface AdminAnalyticsGrowthRow {
  date: string
  brands: number
  agencies: number
  creators: number
}

export interface AdminAnalyticsGrowth {
  /** ISO 8601 `YYYY-MM-DD`, inclusive both ends. */
  range: { start: string; end: string }
  /**
   * Zero-filled daily series. `30d` → 31 rows, `90d` → 91 rows,
   * `all` → variable. FE consumes via
   * `<LineChart data={growth.series}>` with one `<Line>` per series
   * column (matches `invite-activity-chart.tsx`).
   */
  series: AdminAnalyticsGrowthRow[]
}

// =========================================================================
// Top tenants (max 5 — see TRD K8)
// =========================================================================

/**
 * One row per top-ranked brand. Brands with zero active memberships
 * are excluded entirely (INNER-JOIN-style); if fewer than 5 brands
 * have ≥1 active member, the array has fewer than 5 entries.
 */
export interface AdminAnalyticsTopTenant {
  id: number
  name: string
  slug: string
  active_creators: number
  total_memberships: number
  /** ISO 8601 timestamp. */
  created_at: string
}

// =========================================================================
// Page props
// =========================================================================

/**
 * Inertia page props for `Admin/Analytics.tsx`. When `state === "empty"`
 * or `state === "error"`, every data prop is `null`.
 */
export interface AdminAnalyticsProps {
  state: AdminAnalyticsState
  kpis: AdminAnalyticsKpis | null
  overlap: AdminAnalyticsOverlap | null
  growth: AdminAnalyticsGrowth | null
  top_tenants: AdminAnalyticsTopTenant[] | null
}
